import { Injectable } from '@angular/core';
import { BehaviorSubject } from 'rxjs';
import { environment } from '../../../../environments/environment';

export interface WebrtcChannel {
  name: string;
  sessionId: string;
}

export type WebrtcConnectionState = 'disconnected' | 'connecting' | 'connected' | 'error';

@Injectable({
  providedIn: 'root'
})
export class WebrtcAudioService {
  private baseUrl = environment.transcribeWebrtcUrl;
  private ws: WebSocket | null = null;
  private pc: RTCPeerConnection | null = null;
  private audioElement: HTMLAudioElement | null = null;
  private iceServers: RTCIceServer[] = [{ urls: 'stun:stun.l.google.com:19302' }];
  private iceTransportPolicy: RTCIceTransportPolicy = 'all';
  private iceDisconnectTimer: ReturnType<typeof setTimeout> | null = null;
  private connectTimeout: ReturnType<typeof setTimeout> | null = null;
  private intentionalClose = false;

  // Auto-reconnect state
  private currentSessionId: string | null = null;
  private _currentChannelName: string | null = null;
  private reconnectAttempts = 0;
  private reconnectTimer: ReturnType<typeof setTimeout> | null = null;
  private static readonly MAX_RECONNECT_ATTEMPTS = 10;
  private static readonly RECONNECT_DELAY_MS = 2000;
  private static readonly MAX_RECONNECT_DELAY_MS = 30000;
  private static readonly CONNECT_TIMEOUT_MS = 15000;

  // Channel display order (matches transcribe_webrtc_srt_channels)
  private static readonly CHANNEL_ORDER = [
    'ORF1', 'ORF2', 'ORF3', 'ORFS', 'KIDS',
    'ORF-W', 'ORF-NOE', 'ORF-B', 'ORF-OOE', 'ORF-S',
    'ORF-T', 'ORF-SI', 'ORF-V', 'ORF-ST', 'ORF-K'
  ];

  connectionState$ = new BehaviorSubject<WebrtcConnectionState>('disconnected');

  get currentChannelName(): string | null {
    return this._currentChannelName;
  }

  get activeSessionId(): string | null {
    return this.currentSessionId;
  }

  /** Register the shared <audio> element used by this service. The
   *  programplan view owns the element; other consumers (e.g. the
   *  details panel) reuse it without spawning a second player. */
  setAudioElement(el: HTMLAudioElement): void {
    this.audioElement = el;
  }

  async fetchConfig(): Promise<void> {
    try {
      const res = await fetch(`${this.baseUrl}/api/config`);
      const config = await res.json();
      console.log('[WebrtcAudio] Config:', config);
      if (config.iceServers) {
        this.iceServers = config.iceServers;
      }
      if (config.iceTransportPolicy) {
        this.iceTransportPolicy = config.iceTransportPolicy;
      }
    } catch (e) {
      console.warn('[WebrtcAudio] Failed to fetch config, using defaults:', e);
    }
  }

  async fetchSessions(): Promise<WebrtcChannel[]> {
    try {
      const res = await fetch(`${this.baseUrl}/api/sessions`);
      const json = await res.json();
      const sessions = json.data || json;
      return sessions
        .filter((s: any) => s.state === 'running')
        .map((s: any) => ({
          name: this.extractChannelName(s.media_filename || s.id),
          sessionId: s.id
        }))
        .sort((a: WebrtcChannel, b: WebrtcChannel) => {
          const idxA = WebrtcAudioService.CHANNEL_ORDER.indexOf(a.name);
          const idxB = WebrtcAudioService.CHANNEL_ORDER.indexOf(b.name);
          if (idxA !== -1 && idxB !== -1) return idxA - idxB;
          if (idxA !== -1) return -1;
          if (idxB !== -1) return 1;
          return a.name.localeCompare(b.name);
        });
    } catch (e) {
      console.error('[WebrtcAudio] Failed to fetch sessions:', e);
      return [];
    }
  }

  private extractChannelName(mediaFilename: string): string {
    const match = mediaFilename.match(/SRT:\s*(.+)/);
    return match ? match[1].trim() : mediaFilename;
  }

  async connect(sessionId: string, audioEl?: HTMLAudioElement | null, channelName?: string | null): Promise<void> {
    this.cleanup();
    if (audioEl) {
      this.audioElement = audioEl;
    }
    if (!this.audioElement) {
      throw new Error('No audio element registered with WebrtcAudioService');
    }
    this.currentSessionId = sessionId;
    this._currentChannelName = channelName ?? null;
    this.intentionalClose = false;
    this.reconnectAttempts = 0;
    this.connectionState$.next('connecting');

    await this.doConnect(sessionId);
  }

  private async doConnect(sessionId: string): Promise<void> {
    await this.fetchConfig();

    const wsProtocol = location.protocol === 'https:' ? 'wss:' : 'ws:';
    const wsUrl = `${wsProtocol}//${location.host}${this.baseUrl}/ws/${sessionId}`;

    return new Promise<void>((resolve, reject) => {
      // Connect timeout — reject if no SDP offer within 15s
      this.connectTimeout = setTimeout(() => {
        console.error('[WebrtcAudio] Connect timeout after 15s');
        this.cleanup();
        if (!this.intentionalClose) {
          this.scheduleReconnect();
        }
        reject(new Error('Connect timeout'));
      }, WebrtcAudioService.CONNECT_TIMEOUT_MS);

      this.ws = new WebSocket(wsUrl);

      this.ws.onopen = () => {
        console.log('[WebrtcAudio] WebSocket connected');
        this.setupPeerConnection(resolve);
      };

      this.ws.onclose = () => {
        console.log('[WebrtcAudio] WebSocket closed');
        if (!this.intentionalClose) {
          this.scheduleReconnect();
        }
      };

      this.ws.onerror = (err) => {
        console.error('[WebrtcAudio] WebSocket error:', err);
        if (this.connectTimeout) {
          clearTimeout(this.connectTimeout);
          this.connectTimeout = null;
        }
        if (!this.intentionalClose) {
          this.scheduleReconnect();
        }
        reject(err);
      };

      this.ws.onmessage = async (event) => {
        try {
          const msg = JSON.parse(event.data);
          await this.handleMessage(msg, resolve);
        } catch (e) {
          console.error('[WebrtcAudio] Error handling message:', e);
        }
      };
    });
  }

  private scheduleReconnect(): void {
    if (this.intentionalClose || !this.currentSessionId) return;

    if (this.reconnectAttempts >= WebrtcAudioService.MAX_RECONNECT_ATTEMPTS) {
      console.error('[WebrtcAudio] Max reconnect attempts reached');
      this.connectionState$.next('error');
      return;
    }

    const delay = Math.min(
      WebrtcAudioService.RECONNECT_DELAY_MS * Math.pow(1.5, this.reconnectAttempts),
      WebrtcAudioService.MAX_RECONNECT_DELAY_MS
    );

    this.reconnectAttempts++;
    console.log(`[WebrtcAudio] Reconnecting in ${Math.round(delay)}ms (attempt ${this.reconnectAttempts})`);
    this.connectionState$.next('connecting');

    this.reconnectTimer = setTimeout(() => {
      if (this.intentionalClose || !this.currentSessionId) return;
      this.cleanup();
      this.doConnect(this.currentSessionId!).catch(() => {
        // Error handled in doConnect — reconnect scheduled there
      });
    }, delay);
  }

  private setupPeerConnection(resolve: () => void): void {
    console.log('[WebrtcAudio] Creating PeerConnection, policy:', this.iceTransportPolicy);
    this.pc = new RTCPeerConnection({
      iceServers: this.iceServers,
      iceTransportPolicy: this.iceTransportPolicy
    });

    this.pc.ontrack = (event) => {
      console.log('[WebrtcAudio] Received audio track');

      // Increase jitter buffer to 300ms for smooth playback over TURN relay
      const receiver = event.receiver;
      if ('jitterBufferTarget' in receiver) {
        (receiver as any).jitterBufferTarget = 300;
        console.log('[WebrtcAudio] Set jitterBufferTarget to 300ms');
      }

      if (this.audioElement) {
        this.audioElement.srcObject = event.streams[0];
        this.audioElement.play().catch(e => {
          console.warn('[WebrtcAudio] Autoplay blocked:', e);
        });
      }
    };

    this.pc.onicecandidate = (event) => {
      if (event.candidate && this.ws?.readyState === WebSocket.OPEN) {
        this.ws.send(JSON.stringify({
          type: 'ice-candidate',
          candidate: event.candidate.toJSON()
        }));
      }
    };

    this.pc.onconnectionstatechange = () => {
      const state = this.pc?.connectionState;
      console.log('[WebrtcAudio] Connection state:', state);
      if (state === 'connected') {
        this.reconnectAttempts = 0; // Reset on successful connection
        this.connectionState$.next('connected');
      } else if (state === 'failed') {
        this.cleanup();
        if (!this.intentionalClose) {
          this.scheduleReconnect();
        }
      }
    };

    this.pc.oniceconnectionstatechange = () => {
      const iceState = this.pc?.iceConnectionState;
      console.log('[WebrtcAudio] ICE state:', iceState);

      if (iceState === 'connected' || iceState === 'completed') {
        if (this.iceDisconnectTimer) {
          clearTimeout(this.iceDisconnectTimer);
          this.iceDisconnectTimer = null;
        }
      } else if (iceState === 'disconnected') {
        console.warn('[WebrtcAudio] ICE disconnected — waiting 5s for recovery');
        if (this.iceDisconnectTimer) clearTimeout(this.iceDisconnectTimer);
        this.iceDisconnectTimer = setTimeout(() => {
          if (this.pc?.iceConnectionState === 'disconnected') {
            console.error('[WebrtcAudio] ICE did not recover after 5s');
            this.cleanup();
            if (!this.intentionalClose) {
              this.scheduleReconnect();
            }
          }
        }, 5000);
      } else if (iceState === 'failed') {
        if (this.iceDisconnectTimer) {
          clearTimeout(this.iceDisconnectTimer);
          this.iceDisconnectTimer = null;
        }
        this.cleanup();
        if (!this.intentionalClose) {
          this.scheduleReconnect();
        }
      }
    };

    this.ws!.send(JSON.stringify({ type: 'ready' }));
  }

  private async handleMessage(msg: any, resolve: () => void): Promise<void> {
    switch (msg.type) {
      case 'welcome':
        console.log('[WebrtcAudio] Welcome:', msg.message);
        break;

      case 'offer':
        if (!this.pc) return;
        // Clear connect timeout — we got the offer
        if (this.connectTimeout) {
          clearTimeout(this.connectTimeout);
          this.connectTimeout = null;
        }
        await this.pc.setRemoteDescription({ type: 'offer', sdp: msg.sdp });
        const answer = await this.pc.createAnswer();
        await this.pc.setLocalDescription(answer);
        this.ws!.send(JSON.stringify({ type: 'answer', sdp: answer.sdp }));
        resolve();
        break;

      case 'ice-candidate':
        if (!this.pc || !msg.candidate) return;
        const candidateStr = msg.candidate.candidate || '';
        // Skip Docker network candidates (172.16.0.0/12)
        if (candidateStr.includes('typ host') && /\s172\.(1[6-9]|2\d|3[01])\.\d+\.\d+\s/.test(candidateStr)) {
          break;
        }
        try {
          await this.pc.addIceCandidate(new RTCIceCandidate({
            candidate: msg.candidate.candidate,
            sdpMid: msg.candidate.sdpMid?.toString() || '0',
            sdpMLineIndex: msg.candidate.sdpMLineIndex ?? 0
          }));
        } catch (e) {
          console.error('[WebrtcAudio] Error adding ICE candidate:', e);
        }
        break;
    }
  }

  /** Clean up WebRTC/WS resources without changing intentionalClose or currentSessionId */
  private cleanup(): void {
    if (this.connectTimeout) {
      clearTimeout(this.connectTimeout);
      this.connectTimeout = null;
    }
    if (this.iceDisconnectTimer) {
      clearTimeout(this.iceDisconnectTimer);
      this.iceDisconnectTimer = null;
    }
    if (this.pc) {
      this.pc.onconnectionstatechange = null;
      this.pc.oniceconnectionstatechange = null;
      this.pc.ontrack = null;
      this.pc.onicecandidate = null;
      this.pc.close();
      this.pc = null;
    }
    if (this.ws) {
      this.ws.onclose = null;
      this.ws.onerror = null;
      this.ws.onmessage = null;
      this.ws.close();
      this.ws = null;
    }
    if (this.audioElement) {
      this.audioElement.srcObject = null;
    }
  }

  disconnect(): void {
    this.intentionalClose = true;
    this.currentSessionId = null;
    this._currentChannelName = null;

    if (this.reconnectTimer) {
      clearTimeout(this.reconnectTimer);
      this.reconnectTimer = null;
    }

    this.cleanup();
    this.reconnectAttempts = 0;
    this.connectionState$.next('disconnected');
  }
}
